import { motion } from "framer-motion";
import { ScrollReveal } from "./Reveal";

const CHAPTERS = [
  {
    nr: "01", kanji: "空", romaji: "sora", title: "Cerul",
    text: "Un cuvânt scurt, ușor de ținut minte, care sună firesc și în română, și în japoneză. Cerul e locul unde cele două lumi se întâlnesc: același cer deasupra Bucureștiului și a Tokyo-ului.",
  },
  {
    nr: "02", kanji: "間", romaji: "ma", title: "Spațiul dintre lucruri",
    text: "Ateneul e casa muzicii, iar muzica trăiește prin pauze. Japonezii au un cuvânt pentru spațiul dintre note: ma. Golul care face totul să respire.",
  },
  {
    nr: "03", kanji: "一", romaji: "ichi", title: "Primul pas",
    text: "Cea mai mare clădire din București a pornit de la o primă cărămidă. O limbă nouă pornește de la un prim semn. 一 e cel mai simplu kanji: o singură linie — și de aici începe totul.",
  },
  {
    nr: "04", kanji: "門", romaji: "mon", title: "Poarta",
    text: "Arcul de Triumf e poarta orașului. O limbă nouă e tot o poartă: odată ce treci prin ea, lumea arată altfel. Chiar forma caracterului desenează două canaturi deschise.",
  },
  {
    nr: "05", kanji: "道", romaji: "michi", title: "Drumul",
    text: "道 înseamnă drum și, în același timp, calea spre măiestrie: îl regăsești în judō, shodō (caligrafia) sau sadō (ceremonia ceaiului). La SORA, cultura japoneză se învață exact așa — ca un drum.",
  },
];

export default function Despre() {
  return (
    <section id="despre" data-testid="despre-section" className="relative py-20 sm:py-32 overflow-hidden">
      <span aria-hidden className="pointer-events-none select-none absolute left-2 top-24 vertical-rl font-jp text-7xl text-sumi/[0.05] hidden sm:block">
        言葉と文化
      </span>

      <div className="max-w-7xl mx-auto px-4 sm:px-8">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <ScrollReveal>
              <p className="text-xs font-mono tracking-[0.3em] uppercase text-hanko mb-4">Despre SORA</p>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-sumi leading-tight">
                Limba vine împreună cu <span className="italic text-hanko">cultura</span> din spatele ei.
              </h2>
            </ScrollReveal>
            <ScrollReveal delay={0.15}>
              <p className="mt-6 text-charcoal/80 leading-relaxed">
                La SORA nu învățăm doar gramatică și vocabular. Fiecare lecție e o punte între București
                și Japonia: caligrafie (shodō), ceremonia ceaiului (sadō), obiceiuri și felul japonez de
                a privi lumea. Pentru toate vârstele — copii, adolescenți și adulți.
              </p>
            </ScrollReveal>
            <ScrollReveal delay={0.25} className="mt-10">
              <div className="relative aspect-[4/5] max-w-xs group">
                <img
                  src="https://images.unsplash.com/photo-1583868635757-b417c771a41e?crop=entropy&cs=srgb&fm=jpg&q=85"
                  alt="Caligrafie japoneză"
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                  data-testid="despre-calligraphy-image"
                />
                <div className="absolute inset-0 ring-1 ring-sumi/10 pointer-events-none" />
                <span className="absolute -bottom-4 -right-4 bg-hanko text-washi font-jp text-2xl w-14 h-14 flex items-center justify-center rounded-[12px] shadow-lg shadow-hanko/30">
                  書
                </span>
              </div>
            </ScrollReveal>
          </div>

          <div className="lg:col-span-7 flex flex-col divide-y divide-sumi/10">
            {CHAPTERS.map((c, i) => (
              <ScrollReveal key={c.nr} delay={i * 0.06}>
                <motion.div
                  data-testid={`kanji-chapter-${c.romaji}`}
                  whileHover="hover"
                  className="group grid grid-cols-[auto_1fr] sm:grid-cols-[auto_auto_1fr] gap-x-6 sm:gap-x-10 items-baseline py-7 sm:py-9 cursor-default"
                >
                  <span className="font-mono text-xs text-hanko tracking-widest">{c.nr}</span>
                  <motion.span
                    variants={{ hover: { y: -6, color: "#D32F2F" } }}
                    transition={{ type: "spring", stiffness: 300, damping: 18 }}
                    className="font-jp text-4xl sm:text-5xl text-sumi leading-none hidden sm:block"
                  >
                    {c.kanji}
                  </motion.span>
                  <div>
                    <div className="flex items-baseline gap-3 flex-wrap">
                      <span className="font-jp text-3xl text-sumi sm:hidden">{c.kanji}</span>
                      <h3 className="font-serif text-xl sm:text-2xl text-sumi">{c.title}</h3>
                      <span className="font-mono text-xs tracking-widest uppercase text-charcoal/50">{c.romaji}</span>
                    </div>
                    <p className="mt-2 text-sm sm:text-base text-charcoal/70 leading-relaxed max-w-xl">{c.text}</p>
                  </div>
                </motion.div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
